import React, { useEffect, useState, useCallback } from 'react';
import { makeStyles } from 'tss-react/mui';
import { startTimedEvent, trackEvent, trackPageView } from 'app-insights/appInsightsTracking';
import { globalActions } from 'app/ApplicationRoot/Global.redux';
import { pageNames } from 'app/constants/PageNames';
import { getEndOfDate, getStartOfDate } from 'app/helpers/dateHelper';
import { getUserKpis, GetUserLogs } from 'app/services/NotificationServices/NotificationService';
import { getTaskData, getTaskKpi } from 'app/services/TaskServices/TaskServices';
import { selectUser } from 'oidc/user.selectors';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import useDocumentTitle from 'utils/customHooks/useDocumentTitle';
import { customSwitch } from 'utils/customSwitchCase/customSwitch';
import { XGrid } from '../Common/XGrid/XGrid';
import { navigationStickActions } from '../GlobalNavigationMenu/NavigationStick.redux';
import FilterWrapper from './Common/Filters/FilterWrapper';
import KpiMetricGrid from './Common/KPIMetric/KpiMetricGrid';
import {
  defaultNotificationChips,
  defaultNotificationValues,
  getNotificationFilterSpecs,
} from './Notification/Filters/utils';
import {
  getNotificationKpiFilterPayload,
  matchNotificationFilterWithTile,
  matchNotificationTileWithFilter,
} from './Notification/helper';
import { getNotificationGridTitle } from './Notification/KPIMetric/Helper';
import {
  flattenedCommunicationGridRow,
  ReportCommunicationView,
  ReportCommunicationVisibilityModel,
} from './Notification/ReportGrid/GridUtil';
import { defaultTaskChips, defaultTaskValue, getTaskFilterSpecs } from './Task/Filters/utils';
import { getTaskKpiFilterPayload, matchTaskFilterWithTile, matchTaskTileWithFilter } from './Task/helper';
import { getTaskGridTitle } from './Task/KPIMetric/Helper';
import { flattenedTaskGridRow, ReportTaskView, ReportTaskViewVisibilityModel } from './Task/ReportGrid/GridUtil';
import _ from 'lodash';
import axios from 'axios';

const useStyles = makeStyles()((theme, props) => ({
  filter: {
    margin: '15px 0',
  },
}));

export const gridTag = 'Report';

export enum TabTitle {
  communication,
  task,
  otherTab,
}

const PAGE_SIZE = 50;
const PAGE_NUMBER = 1;

export const ReportPage = ({
  displayTab,
  selectedRange,
  selectKpiFilter,
  setSelectKpiFilter,
  selectedKpiMetric,
  setSelectedKpiMetric,
}: {
  displayTab: TabTitle;
  selectedRange: { startDate: Date | string; endDate: Date | string };
  selectKpiFilter: any;
  setSelectKpiFilter: React.Dispatch<any>;
  selectedKpiMetric: string;
  setSelectedKpiMetric: React.Dispatch<any>;
}) => {
  const { t } = useTranslation();
  const { classes, cx } = useStyles();
  const dispatch = useDispatch();
  const user = useSelector(selectUser);
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [totalRecordsCount, setTotalRecordsCount] = useState<number>(0);
  const [columns, setColumns] = useState<any>([]);
  const [defaultColumns, setDefaultColumns] = useState<any>([]);
  const [defaultSortModel, setDefaultSortModel] = useState<any>([]);
  const [xGridSortModel, setxGridSortModel] = useState<any>([]);
  const [gridTitle, setGridTitle] = useState('');
  const [defaultColumnVisibilityModel, setDefaultColumnVisibilityModel] = useState<any>([]);
  const [currentColumnVisibilityModel, setCurrentColumnVisibilityModel] = useState<any>([]);

  useDocumentTitle({ title: pageNames.reports });

  useEffect(() => {
    trackPageView({ name: pageNames.reports });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  //KPI Tiles
  const [KpiMetrics, setKpiMetrics] = useState<any>([]);
  const [pagination, setPagination] = useState({ pageSize: PAGE_SIZE, pageNumber: PAGE_NUMBER });

  const setGridRows = useCallback(
    (response: any) => {
      switch (displayTab) {
        case TabTitle.communication:
          return setRows(flattenedCommunicationGridRow(response));
        case TabTitle.task:
          return setRows(flattenedTaskGridRow(response, user.userInfo?.employeeId));
      }
    },
    [displayTab],
  );

  const getUserLogs = useCallback(
    async filterset => {
      const filterPayload = getNotificationKpiFilterPayload(filterset);
      const getLogsPayload = {
        userId: `${user.userInfo?.employeeId}`,
        pageSize: pagination.pageSize,
        pageNumber: pagination.pageNumber,
        startDate: selectedRange.startDate,
        endDate: selectedRange.endDate,
        ...filterPayload,
      };

      //Removing all Empty properties before making getlogs Query
      Object.keys(getLogsPayload)?.forEach(keys => getLogsPayload[keys] === '' && delete getLogsPayload[keys]);

      try {
        const response = await GetUserLogs(getLogsPayload);
        setLoading(false);
        setGridRows(response?.results);
        setTotalRecordsCount(response?.totalRecords || 0);
      } catch (error) {
        setLoading(false);
        dispatch(
          globalActions.setSnackBar({
            message: t('report.failedToFetch'),
            severity: 'error',
          }),
        );
      }
    },
    [
      user.userInfo,
      pagination.pageSize,
      pagination.pageNumber,
      selectedRange.startDate,
      selectedRange.endDate,
      setGridRows,
      dispatch,
      t,
    ],
  );

  const getTaskLogs = useCallback(
    async filterset => {
      if (!filterset.hasOwnProperty('category')) return;
      const filterPayload = getTaskKpiFilterPayload(filterset);
      const getLogsPayload = {
        assignedTo: [user.userInfo?.employeeId],
        candidateId: [],
        pageSize: pagination.pageSize,
        pageNumber: pagination.pageNumber,
        dueDateStart: getStartOfDate(selectedRange.startDate),
        dueDateEnd: getEndOfDate(selectedRange.endDate),
        ...filterPayload,
      };

      //Removing all Empty properties before making getlogs Query
      Object.keys(getLogsPayload)?.forEach(keys => getLogsPayload[keys] === '' && delete getLogsPayload[keys]);

      try {
        const cancelTokenSource = axios.CancelToken.source();
        const response = await getTaskData(getLogsPayload, cancelTokenSource.token);
        setLoading(false);
        setGridRows(response?.data?.items);
        setTotalRecordsCount(response?.data?.paging?.total || 0);
      } catch (error) {
        setLoading(false);
        dispatch(
          globalActions.setSnackBar({
            message: t('report.failedToFetch'),
            severity: 'error',
          }),
        );
      }
    },
    [
      user.userInfo,
      pagination.pageSize,
      pagination.pageNumber,
      selectedRange.startDate,
      selectedRange.endDate,
      setGridRows,
      dispatch,
      t,
    ],
  );

  useEffect(() => {
    dispatch(navigationStickActions.setSelectedMenuByKey('report'));
    trackEvent({ type: 'click', name: 'Global KPI page Access', properties: { userRole: user.userInfo?.roles } });
    startTimedEvent('globalKPIpageAccess');
    setColumns(getTabColumn(displayTab));
    setDefaultColumns(getTabColumn(displayTab));
    setDefaultSortModel(getDefaultSort(displayTab));
    setxGridSortModel(getDefaultSort(displayTab));
    setDefaultColumnVisibilityModel(getTabVisibilityModel(displayTab));
    setCurrentColumnVisibilityModel(getTabVisibilityModel(displayTab));

    const initialMetricsPayload = {
      userId: `${user.userInfo?.employeeId}`,
      startDate: getStartOfDate(selectedRange.startDate),
      endDate: getEndOfDate(selectedRange.endDate),
    };

    getKPIMetrics(initialMetricsPayload);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRange, displayTab]);

  useEffect(
    () => {
      setLoading(true);
      if (displayTab === 0) {
        setGridTitle(getNotificationGridTitle(selectedKpiMetric));
        getUserLogs(selectKpiFilter);
      } else {
        setGridTitle(getTaskGridTitle(selectedKpiMetric) || 'Tasks');
        getTaskLogs(selectKpiFilter);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedKpiMetric, getUserLogs, getTaskLogs, selectKpiFilter],
  );

  const handleRefreshGrid = () => {
    setLoading(true);
    if (displayTab === 0) {
      setGridTitle(getNotificationGridTitle(selectedKpiMetric));
      getUserLogs(selectKpiFilter);
    } else {
      setGridTitle(getTaskGridTitle(selectedKpiMetric) || 'Tasks');
      getTaskLogs(selectKpiFilter);
    }
  };

  const getTabColumn = customSwitch({
    [TabTitle.communication]: ReportCommunicationView(t),
    [TabTitle.task]: ReportTaskView(t),
  })({});

  const getDefaultSort = customSwitch({
    [TabTitle.communication]: [{ field: 'date', sort: 'desc' }],
    [TabTitle.task]: [{ field: 'dueDate', sort: 'desc' }],
  })({});

  const getTabVisibilityModel = customSwitch({
    [TabTitle.communication]: ReportCommunicationVisibilityModel,
    [TabTitle.task]: ReportTaskViewVisibilityModel,
  })({});

  const handleKpiMetricClick = (label: string) => {
    const matchDisplayTabFilter = customSwitch({
      [TabTitle.communication]: matchNotificationTileWithFilter(label),
      [TabTitle.task]: matchTaskTileWithFilter(label),
    })(null);
    setSelectedKpiMetric(label);
    setSelectKpiFilter(matchDisplayTabFilter(displayTab));
  };

  const getKPIMetrics = async (payload: any) => {
    let kpiData: any = {};

    if (displayTab === TabTitle.communication) {
      kpiData = await getUserKpis(payload).catch(err =>
        dispatch(
          globalActions.setSnackBar({
            message: 'Failed to fetch user KPI metrics.',
            severity: 'error',
          }),
        ),
      );
      setKpiMetrics(kpiData && kpiData?.find(Boolean) !== undefined && kpiData?.find(Boolean).channelKpis);
    }
    if (displayTab === TabTitle.task) {
      kpiData = await getTaskKpi(payload).catch(err =>
        dispatch(
          globalActions.setSnackBar({
            message: 'Failed to fetch task KPI metrics.',
            severity: 'error',
          }),
        ),
      );
      setKpiMetrics(kpiData && kpiData.channelKpis);
    }
    return kpiData;
  };

  const getDefaultFilterChips = customSwitch({
    [TabTitle.communication]: defaultNotificationChips,
    [TabTitle.task]: defaultTaskChips,
  })({});

  const getDefaultFilterValue = customSwitch({
    [TabTitle.communication]: defaultNotificationValues,
    [TabTitle.task]: defaultTaskValue,
  })({});

  const onPageChange = (currentPage: number) => {
    setPagination(props => ({ ...props, pageNumber: currentPage + 1 }));
  };

  const onPageSizeChange = params => {
    setPagination({ pageSize: params, pageNumber: 1 });
  };

  const onSortModelChange = model => {
    setxGridSortModel(model);
  };

  const _onFilterChange = value => {
    setSelectKpiFilter(value);
    if (displayTab === TabTitle.communication) setSelectedKpiMetric(matchNotificationFilterWithTile(value));
    else if (displayTab === TabTitle.task) setSelectedKpiMetric(matchTaskFilterWithTile(value));
  };

  const ReportGrid = React.useMemo(() => {
    return (
      <>
        {rows && (
          <XGrid
            sortModel={xGridSortModel}
            defaultSortModel={defaultSortModel}
            onSortModelChange={onSortModelChange}
            defaultColumns={defaultColumns}
            loading={loading}
            autoHeight={false}
            height={420}
            gridTag={gridTitle}
            rows={rows || []}
            columns={columns}
            defaultColumnVisibilityModel={defaultColumnVisibilityModel}
            columnVisibilityModel={currentColumnVisibilityModel}
            rowCount={totalRecordsCount}
            rowsPerPageOptions={[50, 100, 150, 200]}
            defaultRowsPerPage={pagination.pageSize || 50}
            rowCountDisplay={totalRecordsCount}
            page={pagination.pageNumber - 1}
            pageSize={pagination.pageSize}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
            title={gridTitle}
            disableSelectionOnClick
            paginationMode="server"
            onColumnVisibilityModelChange={(model, details) => {
              setCurrentColumnVisibilityModel(_.cloneDeep(model));
            }}
            toolbarProps={{
              refreshButton: true,
              refreshGrid: () => {
                handleRefreshGrid();
              },
              resetColumns: () => {
                setColumns(getTabColumn(displayTab));
                setDefaultColumns(getTabColumn(displayTab));
                setDefaultSortModel(getDefaultSort(displayTab));
                setxGridSortModel(getDefaultSort(displayTab));
                setDefaultColumnVisibilityModel(getTabVisibilityModel(displayTab));
                setCurrentColumnVisibilityModel(getTabVisibilityModel(displayTab));
                dispatch(
                  globalActions.setSnackBar({
                    message: 'Columns have been reset.',
                    severity: 'success',
                  }),
                );
              },
            }}
          />
        )}
      </>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    columns,
    defaultColumns,
    defaultSortModel,
    gridTitle,
    loading,
    rows,
    totalRecordsCount,
    t,
    displayTab,
    xGridSortModel,
    currentColumnVisibilityModel,
    defaultColumnVisibilityModel,
    pagination,
  ]);

  const getFilterSpecs = customSwitch({
    [TabTitle.communication]: getNotificationFilterSpecs(),
    [TabTitle.task]: getTaskFilterSpecs(),
  })([]);

  return (
    <>
      <KpiMetricGrid
        title={displayTab}
        kpiMetrics={KpiMetrics}
        onClickHandler={handleKpiMetricClick}
        selectedMetric={selectedKpiMetric}
      />

      <div className={classes.filter}>
        <FilterWrapper
          title={displayTab}
          selectedFilterValue={selectKpiFilter}
          onFilterChange={_onFilterChange}
          defaultChip={getDefaultFilterChips(displayTab)}
          defaultValues={getDefaultFilterValue(displayTab)}
          filterSpec={getFilterSpecs(displayTab)}
        />
      </div>
      {ReportGrid}
    </>
  );
};
